/**
 * Cross-Pipeline Reasoner
 * Analyzes patterns across chat, uploads, memory, and content to determine user state
 * and generate personalized crossInsight messages for focus cards
 */

/**
 * Determine user state based on memory and activity patterns
 * @param {Object} memory - User memory object from memoryEngine
 * @param {Object} pipelineResults - Results from all three pipelines
 * @returns {string} User state: "overcollector", "scatter", "visual-mode", "seeking-clarity", "mixed"
 */
function determineUserState(memory, pipelineResults) {
  if (!memory) {
    return 'mixed';
  }

  const {
    upload_concentration_score,
    abandoned_threads,
    conversation_switch_rate,
    content_by_channel_7d,
    top_words_7d,
  } = memory;

  // Overcollector: High upload concentration but many abandoned threads
  if (upload_concentration_score > 0.7 && abandoned_threads?.length > 2) {
    return 'overcollector';
  }

  // Scatter: High conversation switch rate (jumping between threads)
  if (conversation_switch_rate > 0.6) {
    return 'scatter';
  }

  // Visual-mode: Instagram dominates other channels by 2x
  const instagramCount = content_by_channel_7d?.Instagram || 0;
  const otherChannelsTotal = Object.entries(content_by_channel_7d || {})
    .filter(([channel]) => channel !== 'Instagram')
    .reduce((sum, [, count]) => sum + count, 0);
  
  if (instagramCount > 0 && instagramCount > otherChannelsTotal * 2) {
    return 'visual-mode';
  }

  // Seeking-clarity: Top words include planning/focus keywords
  const clarityWords = ['plan', 'structure', 'focus', 'organize', 'clarify', 'direction', 'goal', 'purpose'];
  const topWords = top_words_7d || [];
  const hasClarityWords = topWords.some(word => clarityWords.includes(word.toLowerCase()));
  
  if (hasClarityWords) {
    return 'seeking-clarity';
  }

  return 'mixed';
}

/**
 * Generate crossInsight message based on user state
 * @param {string} userState - User state from determineUserState
 * @param {Object} memory - User memory object
 * @param {Object} pipelineResults - Results from all pipelines
 * @returns {string|null} CrossInsight message or null if no insight
 */
function generateCrossInsight(userState, memory, pipelineResults) {
  const { activeCard, suggestedCard, insightCard } = pipelineResults;

  switch (userState) {
    case 'overcollector':
      return "You're spreading your attention thin. Pick one thread to commit to for the next 24 hours.";

    case 'scatter':
      return "You jump between ideas fast. Let's anchor you in one clean direction.";

    case 'visual-mode':
      return "Your attention is visual-heavy this week. Want a thread or story arc from it?";

    case 'seeking-clarity':
      return "You keep circling clarity questions. Want me to build you a structure?";

    case 'mixed':
    default:
      // For mixed state, only add insight if there are specific patterns
      if (memory?.abandoned_threads?.length > 3) {
        return "You have several unfinished threads. Time to close one out or start fresh.";
      }
      if (memory?.conversation_switch_rate > 0.4) {
        return "You're exploring multiple directions. Pick one to go deeper on.";
      }
      return null;
  }
}

/**
 * Main function to analyze and generate crossInsight for all cards
 * @param {Object} memory - User memory object
 * @param {Object} pipelineResults - Results from all three pipelines
 * @returns {Object} Object with userState and crossInsight for each card type
 */
export function analyzeAndEnrich(memory, pipelineResults) {
  const userState = determineUserState(memory, pipelineResults);
  const crossInsight = generateCrossInsight(userState, memory, pipelineResults);

  console.log('[CrossReasoner] state:', userState);
  if (crossInsight) {
    console.log('[CrossReasoner] crossInsight generated:', crossInsight);
  }

  return {
    userState,
    crossInsight, // Single crossInsight that can be applied to all cards or specific ones
  };
}

/**
 * Apply crossInsight to a specific card
 * @param {Object} card - Card object
 * @param {string} crossInsight - CrossInsight message
 * @returns {Object} Card with crossInsight added
 */
export function enrichCardWithCrossInsight(card, crossInsight) {
  if (!card || !crossInsight) {
    return card;
  }

  return {
    ...card,
    crossInsight,
  };
}

